import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  computed,
  inject,
} from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import { Pwa } from '@qtek/libs/app-component';
import { AuthUserStore } from '@qtek/libs/auth-user';
import { PersonDocumentReferenceStore, UserStore } from '@qtek/libs/store';
import {
  DocumentReferenceEntityType,
  EXTERNAL_LINKS,
  HTTP_BASE_URL,
} from '@qtek/shared/models';
import { buildDocumentServingUrl, isNullable } from '@qtek/shared/utils';

@Component({
  selector: 'qt-avatar-menu',
  templateUrl: './avatar-menu.component.html',
  styleUrls: ['./avatar-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AvatarMenuComponent implements OnInit {
  private readonly baseUrl = inject(HTTP_BASE_URL);
  pwa = inject(Pwa);
  private readonly personDocumentReferenceStore = inject(
    PersonDocumentReferenceStore
  );
  private readonly userStore = inject(UserStore);
  private readonly authUserStore = inject(AuthUserStore);
  private readonly router = inject(Router);

  @Output()
  public framesOpen: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  public logoutClicked: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  public personalInfoClicked: EventEmitter<void> = new EventEmitter<void>();

  @Input()
  public sidenav: MatSidenav;

  @Input({ transform: coerceBooleanProperty })
  public isHiddenUserName = false;

  public readonly personalAvatarUrl = computed(() => {
    const avatarReference = this.personDocumentReferenceStore.avatar();

    if (isNullable(avatarReference) || !avatarReference?._id) {
      return '';
    }

    const isSmallThumbnailExist =
      avatarReference?.document?.extensions?.includes('sm-thb');

    return buildDocumentServingUrl({
      baseUrl: this.baseUrl,
      referenceType: DocumentReferenceEntityType.PERSON,
      referenceId: avatarReference._id,
      extension: isSmallThumbnailExist ? 'sm-thb' : undefined,
    });
  });
  public readonly issueReportLink = EXTERNAL_LINKS.ISSUE_REPORT;

  readonly userData = computed(() => {
    const person = this.userStore.currentPerson();
    const company = this.userStore.mainCompany();
    const userRelation = this.userStore.userRelation();
    return {
      name: person?.name || '',
      company: company?.companyName || '',
      email: userRelation?.email || '',
    };
  });

  ngOnInit(): void {
    this.personDocumentReferenceStore.loadAvatar();
  }

  getCompanyName(company: any): string {
    if (!company) {
      return '';
    }

    return company.cmp?.companyName ? company.cmp.companyName : company.email;
  }

  getCompanyEmail(company: any): string {
    return company.email !== '' ? company.email : '';
  }

  closeSideNave(): void {
    if (this.sidenav && this.sidenav.opened) {
      this.sidenav.close();
    }
  }

  openSettings() {
    this.router.navigate(['stp']);
  }

  public onLogout(): void {
    this.logoutClicked.emit();
  }

  public onFramesOpen(): void {
    this.framesOpen.emit();
  }

  handleLogout(): void {
    this.authUserStore.logout();
  }

  /**
   * Returns false if user first or last name is null or undefined
   *
   * @param {string} name
   * @private
   */
  private validateName(name: string): boolean {
    const trimmedName = name.trim();
    return (
      !trimmedName?.includes('null') && !trimmedName?.includes('undefined')
    );
  }
}
